import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_SITE_ORIGIN = "https://asasiasa.com";
const INDEXABLE_HOSTS = new Set(["asasiasa.com", "www.asasiasa.com"]);

const SITE_ORIGIN = (
  import.meta.env.VITE_PUBLIC_SITE_URL || DEFAULT_SITE_ORIGIN
).replace(/\/+$/, "");

const normalizePath = (pathname) => {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.replace(/\/+$/, "");
};

const getManagedElement = (selector, createElement) => {
  const existingElement = document.head.querySelector(selector);

  if (existingElement) {
    return existingElement;
  }

  const nextElement = createElement();
  nextElement.dataset.asasiasaSeo = "true";
  document.head.appendChild(nextElement);
  return nextElement;
};

const setMetaName = (name, content) => {
  const element = getManagedElement(`meta[name="${name}"]`, () => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", name);
    return meta;
  });

  element.setAttribute("content", content);
};

const setCanonical = (href) => {
  const element = getManagedElement('link[rel="canonical"]', () => {
    const link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    return link;
  });

  element.setAttribute("href", href);
};

const removeCanonical = () => {
  const element = document.head.querySelector('link[rel="canonical"]');

  if (element?.dataset.asasiasaSeo === "true") {
    element.remove();
  }
};

const getSeoConfig = (pathname) => {
  const normalizedPath = normalizePath(pathname);

  if (normalizedPath === "/tutorial") {
    return {
      title: "Asasiasa | Learn, take notes, and repeat",
      description:
        "Pick a subject, read public lessons, take notes, repeat them, and use flashcards with Asasiasa.",
      canonicalPath: "/tutorial",
      indexable: true,
    };
  }

  if (normalizedPath === "/terms") {
    return {
      title: "Terms | Asasiasa",
      description: "Read the Asasiasa terms of service.",
      canonicalPath: "/terms",
      indexable: true,
    };
  }

  if (normalizedPath === "/privacy") {
    return {
      title: "Privacy | Asasiasa",
      description: "Read the Asasiasa privacy policy.",
      canonicalPath: "/privacy",
      indexable: true,
    };
  }

  if (normalizedPath === "/contact") {
    return {
      title: "Contact | Asasiasa",
      description: "Contact the Asasiasa team.",
      canonicalPath: "/contact",
      indexable: true,
    };
  }

  if (/^\/public\/lesson\/[^/]+$/.test(normalizedPath)) {
    return {
      title: "Public lesson | Asasiasa",
      description: "Read this public lesson on Asasiasa.",
      canonicalPath: normalizedPath,
      indexable: true,
    };
  }

  if (/^\/public\/notes\/[^/]+$/.test(normalizedPath)) {
    return {
      title: "Public notes | Asasiasa",
      description: "Review public notes for this Asasiasa lesson.",
      canonicalPath: normalizedPath,
      indexable: true,
    };
  }

  if (/^\/public\/flashcards\/[^/]+$/.test(normalizedPath)) {
    return {
      title: "Public flashcards | Asasiasa",
      description: "Study public flashcards for this Asasiasa lesson.",
      canonicalPath: normalizedPath,
      indexable: true,
    };
  }

  if (/^\/users\/[^/]+$/.test(normalizedPath)) {
    return {
      title: "Public profile | Asasiasa",
      description: "View this public Asasiasa learning profile.",
      canonicalPath: normalizedPath,
      indexable: true,
    };
  }

  if (/^\/communities\/[^/]+\/public-view$/.test(normalizedPath)) {
    return {
      title: "Community profile | Asasiasa",
      description: "View this public Asasiasa community profile.",
      canonicalPath: normalizedPath,
      indexable: true,
    };
  }

  return {
    title: "Asasiasa",
    description:
      "Asasiasa helps you learn through lessons, notes, flashcards, and study communities.",
    canonicalPath: null,
    indexable: false,
  };
};

const SeoHead = () => {
  const location = useLocation();
  const config = useMemo(
    () => getSeoConfig(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    const isIndexableHost = INDEXABLE_HOSTS.has(window.location.hostname);
    const robots = config.indexable && isIndexableHost ? "index,follow" : "noindex,follow";

    document.title = config.title;
    setMetaName("description", config.description);
    setMetaName("robots", robots);

    if (config.canonicalPath) {
      setCanonical(`${SITE_ORIGIN}${config.canonicalPath}`);
    } else {
      removeCanonical();
    }
  }, [config]);

  return null;
};

export default SeoHead;
